import {
  DefaultProfileTier,
  DefaultProfileTierLiteral,
} from "../MagicTypes.js";
import { zGuard } from "../zod/utils.js";

import {
  EffortLevel,
  EffortLevelLiteral,
  ModelSpeed,
  ModelSpeedLiteral,
} from "./effortLevel.js";
import { Model, ModelLiteral } from "./Model.js";

/**
 * Resolve a model name to an active model for GraphQL and runtime use.
 * Returns null for values that are not recognized active Model members.
 */
export function resolveActiveModelName(
  model: string | null | undefined,
): Model | null {
  // Some historical threads may still have model set to null.
  // TODO(@zkirby): clean up this logic after >3 months when historical
  // threads have been gc'd.
  if (model == null) return null;
  return zGuard(ModelLiteral, model) ? model : null;
}

/**
 * Find the matching, currently-usable picker option for a model name. Returns
 * `undefined` when `model` is null/"auto" or when the model is absent or
 * disabled in `options`.
 */
export function findAvailableModelOption<
  T extends { model: string | null; disabled: boolean },
>(options: readonly T[], model: Model | null): T | undefined {
  if (model == null) {
    return undefined;
  }
  return options.find((opt) => opt.model === model && !opt.disabled);
}

/**
 * Resolve a stored/selected (model, effort, speed) against the org's currently
 * available picker options, mirroring the server's `resolveModelConfig`:
 * preserves Auto selections and otherwise returns Auto unless the model is
 * available and the effort is supported. For fast, a fast variant must exist
 * for that effort (otherwise
 * the speed drops to standard). Shared by the org default-model setting row, the
 * prompt-bar seeding hook, and anywhere else a stored selection is validated on
 * read so the answer never diverges.
 */
export function resolveAvailableModelSelection<
  T extends {
    model: string | null;
    disabled: boolean;
    supportedEfforts: readonly EffortLevel[];
    supportedFastEfforts?: readonly EffortLevel[] | null;
  },
>(selection: ModelSelection, options: readonly T[]): ModelSelection {
  if (selection.auto) return selection;
  const available = findAvailableModelOption(options, selection.model);
  if (
    available == null ||
    selection.effortLevel == null ||
    !available.supportedEfforts.includes(selection.effortLevel)
  ) {
    return AUTO_MODEL_SELECTION;
  }
  const shouldUseFast =
    selection.speed === "fast" &&
    (available.supportedFastEfforts ?? []).includes(selection.effortLevel);
  return {
    auto: false,
    defaultProfileTier: null,
    model: selection.model,
    effortLevel: selection.effortLevel,
    speed: shouldUseFast ? "fast" : null,
  };
}

export type ThreadModelFields = {
  auto: boolean | null;
  model: string | null;
  effortLevel: string | null;
  defaultProfileTier?: string | null;
  speed?: string | null;
};

/** Converts stored thread fields to the legacy picker model, where null means Auto. */
export const threadPickerModel = (
  thread: Partial<ThreadModelFields>,
): Model | null =>
  toLegacyMutationSelection(normalizeModelSelection(thread)).model;

/** Auto/profile describe the choice; model, effort, and speed retain execution settings. */
export type ModelSelection = {
  auto: boolean;
  defaultProfileTier: DefaultProfileTier | null;
  model: Model | null;
  effortLevel: EffortLevel | null;
  speed: ModelSpeed | null;
};

export const AUTO_MODEL_SELECTION: ModelSelection = {
  auto: true,
  defaultProfileTier: null,
  model: null,
  effortLevel: null,
  speed: null,
};

/**
 * Normalizes stored fields into a complete selection. Missing or retired models
 * are treated as Auto; recognized models retain their execution settings.
 */
export function normalizeModelSelection(
  selection: Partial<ThreadModelFields>,
): ModelSelection {
  const model = resolveActiveModelName(selection.model);
  const auto = selection.auto === true || model == null;
  // Effort and speed belong to the stored model; drop them when it is retired.
  return {
    auto,
    defaultProfileTier:
      auto && zGuard(DefaultProfileTierLiteral, selection.defaultProfileTier)
        ? selection.defaultProfileTier
        : null,
    model,
    effortLevel:
      model != null && zGuard(EffortLevelLiteral, selection.effortLevel)
        ? selection.effortLevel
        : null,
    speed:
      model != null && zGuard(ModelSpeedLiteral, selection.speed)
        ? selection.speed
        : null,
  };
}

/**
 * Converts a selection to the args of mutations that have no `auto` arg and
 * read a null model as Auto. Sending Auto's resolved settings would look like
 * an explicit pick, so they are nulled.
 */
export function toLegacyMutationSelection(
  selection: ModelSelection,
): Omit<ModelSelection, "auto"> {
  return {
    model: selection.auto ? null : selection.model,
    effortLevel: selection.auto ? null : selection.effortLevel,
    speed: selection.auto ? null : selection.speed,
    defaultProfileTier: selection.auto ? selection.defaultProfileTier : null,
  };
}
